import { matchesUrl, searchUrl, sourcingUrl } from "@findemdev/findem-helpers";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  IGetAssignmentRoleDetailsParams,
  IGetAssignmentRoleDetailsResponse,
} from "../components/CredentialApp/types/assignment";
import {
  ICreateCompanyCompMacrosPayload,
  ICreateCompanyCompMacrosResponse,
} from "../components/CompanyBuilder/types/api";
import {
  CandidateTagsByPrid,
  IAttrCandidateTagsBulkUpdateParams,
  IAttrCandidateTagsParams,
  IGetSearchByIdParams,
  IGetRRAProfilesParams,
  IGetIcpInsightsParams,
  IGetPeopleListIcpsParams,
  IGetAttrCandidateTagsParams,
  IShortlistInfoByPridsParams,
  IShortlistInfoByPridsResponse,
  IIcpInsightsResults,
  IIcpInsightsUpdateResponse,
  IIcpSettings,
  IIcpSettingsPayload,
  IMultiMatchesParams,
  IMultiMatchesResponse,
  IPeopleListImportProfileResponse,
  IPeopleListIcpsResponse,
  IPeopleListMatchesResponse,
  IPeopleListScorecardDocument,
  IPeopleListScorecardEvaluationResult,
  IPeopleListScorecardGenerateParams,
  IPeopleListScorecardProjectDetails,
  IPeopleListScorecardProjectDetailsSaveParams,
  IPeopleListScorecardSaveParams,
  IPeopleListSearch,
  IPeopleListShortlistProfileParams,
  IProfileIdByCandidateRef,
  IProfileIdByCandidateRefParams,
  IProfileIdByCandidateRefResponse,
  IUpdateIcpInsightsParams,
} from "../types/searchRequest";
import type { IImportProfileParams, IProfile } from "../types/profile";

const getProfileEventsUrl = ({
  sourceId,
  eventTab,
  ignoreUser,
  includeCandidateTags,
  joid,
}: {
  sourceId: string;
  eventTab?: string;
  ignoreUser?: boolean;
  includeCandidateTags?: boolean;
  joid?: string;
}) => {
  const params = new URLSearchParams({
    source_id: sourceId,
  });

  if (eventTab) params.set("view_source", eventTab);
  if (ignoreUser !== undefined) params.set("ignore_user", String(ignoreUser));
  if (includeCandidateTags !== undefined) {
    params.set("include_candidate_tags", String(includeCandidateTags));
  }
  if (joid) params.set('joid', joid);

  return `${matchesUrl}/hm/api/attribution_event/search_views?${params.toString()}`;
};

const appendJoidToUrl = ({
  joid,
  url,
}: {
  joid?: string;
  url: string;
}): string => {
  if (!joid) return url;

  const queryPrefix = url.includes('?') ? '&' : '?';

  return `${url}${queryPrefix}${new URLSearchParams({ joid }).toString()}`;
};

const getJoidFromBody = ({ body }: { body: unknown }): string | undefined =>
  typeof body === 'object' && body !== null && 'joid' in body
    ? String((body as { joid?: unknown }).joid || '') || undefined
    : undefined;

const getIcpInsightsRequestBody = (params: IGetIcpInsightsParams) => ({
  channel: "external",
  filters: params.filters ?? [],
  icp_id: params.icpId,
  include_past_versions: params.include_past_versions ?? false,
  joid: params.joid,
  load_more: params.load_more ? true : false,
});

const getSearchByIdRequestBody = (params: IGetSearchByIdParams) => ({
  icpid: params.icpid,
  joid: params.joid,
  sync_profile_refresh_status: params.sync_profile_refresh_status ?? true,
  type: params.type ?? "ByIcpId",
});

const PEOPLE_LIST_ICP_QUERY_LIMIT = 10;

const getLimitedPeopleListIcpIds = ({
  icpIds,
}: {
  icpIds: string[];
}) => {
  if (icpIds.length <= PEOPLE_LIST_ICP_QUERY_LIMIT) return icpIds;

  return icpIds.slice(0, PEOPLE_LIST_ICP_QUERY_LIMIT);
};

const warnIfPeopleListIcpIdsDropped = ({
  icpIds,
}: {
  icpIds: string[];
}) => {
  const droppedCount = icpIds.length - PEOPLE_LIST_ICP_QUERY_LIMIT;

  if (droppedCount <= 0) return;

  console.warn(
    `Dropping ${droppedCount} People List ICP ids above the ${PEOPLE_LIST_ICP_QUERY_LIMIT} ICP limit.`
  );
};

const getShortlistInfoByPridsResponse = (
  response: unknown
): IShortlistInfoByPridsResponse => {
  if (
    typeof response === "object" &&
    response !== null &&
    !Array.isArray(response) &&
    "sl_info" in response
  ) {
    const shortlistInfo = (response as { sl_info?: unknown }).sl_info;

    if (
      typeof shortlistInfo === "object" &&
      shortlistInfo !== null &&
      !Array.isArray(shortlistInfo)
    ) {
      return shortlistInfo as IShortlistInfoByPridsResponse;
    }
  }

  return response as IShortlistInfoByPridsResponse;
};

export const searchesApi = createApi({
  reducerPath: "search",
  baseQuery: fetchBaseQuery({
    baseUrl: searchUrl,
  }),
  tagTypes: [
    "getAttrCandidateTags",
    "searchSettings",
    "searchCandidateResults",
    "PeopleListScorecard",
    "PeopleListScorecardProjectDetails",
    "PeopleListIcps",
    "PeopleListShortlistInfo",
  ],
  endpoints: (build) => ({
    // Company Builder - start
    createCompanyCompMacros: build.mutation<
      ICreateCompanyCompMacrosResponse,
      ICreateCompanyCompMacrosPayload
    >({
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
        });

        return {
          url: `pub/api/create_compound_macro?${searchParams.toString()}`,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: params,
        };
      },
    }),
    // Company Builder - end
    // RRA - start
    getAssignmentRoleDetails: build.query<
      IGetAssignmentRoleDetailsResponse,
      IGetAssignmentRoleDetailsParams
    >({
      query: (params) => {
        const searchParams = new URLSearchParams({
          joid: params.joid,
          project_ids: params.projectIds.join(", "),
        });

        return {
          url: `pub/api/beacon/project/detail?${searchParams.toString()}`,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          method: "GET",
        };
      },
    }),
    // RRA - end
    getIcpInsights: build.query<IIcpInsightsResults, IGetIcpInsightsParams>({
      providesTags: (_result, _error, params) => [
        {
          id: params.icpId,
          type: "searchCandidateResults",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: "pub/api/icp-results/results",
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: getIcpInsightsRequestBody(params),
      }),
    }),
    getSearchById: build.query<IPeopleListSearch, IGetSearchByIdParams>({
      providesTags: (_result, _error, params) =>
        params.icpid
          ? [
              {
                id: params.icpid,
                type: "PeopleListIcps" as const,
              },
            ]
          : [],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: "pub/api/icp",
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: getSearchByIdRequestBody(params),
      }),
    }),
    getPeopleListIcps: build.query<
      IPeopleListIcpsResponse,
      IGetPeopleListIcpsParams
    >({
      providesTags: (_result, _error, { icpIds }) =>
        getLimitedPeopleListIcpIds({ icpIds }).map((icpId) => ({
          id: icpId,
          type: "PeopleListIcps" as const,
        })),
      query: (params) => {
        warnIfPeopleListIcpIdsDropped({ icpIds: params.icpIds });
        const icpIds = getLimitedPeopleListIcpIds({ icpIds: params.icpIds });

        return {
          url: appendJoidToUrl({
            joid: params.joid,
            url: `${matchesUrl}/hm/api/icp`,
          }),
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: {
            icpids: icpIds,
            joid: params.joid,
            type: "Icps",
          },
        };
      },
    }),
    updateIcpInsights: build.mutation<
      IIcpInsightsUpdateResponse,
      IUpdateIcpInsightsParams
    >({
      invalidatesTags: (_result, _error, params) => [
        {
          id: params.icp_id,
          type: "searchCandidateResults",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: "pub/api/icp-results/results/update",
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: params,
      }),
    }),
    getMatchesAwait: build.mutation<IPeopleListMatchesResponse, unknown>({
      query: (body) => ({
        url: appendJoidToUrl({
          joid: getJoidFromBody({ body }),
          url: "pub/api/matchesV2",
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: {
          ...(body as Record<string, unknown>),
          prioritize_freshness: true,
        },
      }),
    }),
    getMultiMatches: build.query<IMultiMatchesResponse, IMultiMatchesParams>({
      query: (body) => ({
        url: appendJoidToUrl({
          joid: body.joid,
          url: "pub/api/multi_matchesV2",
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body,
      }),
    }),
    getRRAProfiles: build.query<
      Record<string, IProfile>,
      IGetRRAProfilesParams
    >({
      query: (body) => ({
        url: appendJoidToUrl({ joid: body.joid, url: "pub/api/profile" }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body,
      }),
    }),
    getProfileIdByCandidateRef: build.query<
      Record<string, IProfileIdByCandidateRef>,
      IProfileIdByCandidateRefParams
    >({
      query: ({
        joid,
        prids,
        proj,
        preferSandbox,
        preferCurrentExp,
        onlyLinkedinExtRefs,
      }) => ({
        url: `${matchesUrl}/hm/api/profile/profile-by-candidate-ref`,
        credentials: "include",
        method: "POST",
        params: {
          joid,
        },
        body: {
          prids,
          proj: proj ?? { ref: 1, "candidate.linkedin": 1 },
          prefer_sandbox: preferSandbox ?? false,
          prefer_current_exp: preferCurrentExp ?? false,
          only_linkedin_ext_refs: onlyLinkedinExtRefs ?? false,
        },
      }),
      transformResponse: (response: IProfileIdByCandidateRefResponse) =>
        response.results,
    }),
    getShortlistInfoByPrids: build.query<
      IShortlistInfoByPridsResponse,
      IShortlistInfoByPridsParams
    >({
      providesTags: (_result, _error, params) => [
        {
          id: params.searchId || params.prids.join("|"),
          type: "PeopleListShortlistInfo",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `${matchesUrl}/hm/api/shortlist_info_by_prids`,
        }),
        credentials: "include",
        method: "POST",
        body: {
          for_restricted_icps: params.forRestrictedIcps,
          for_visible_icps: params.searchId ? [params.searchId] : [],
          is_sandbox: params.isSandbox,
          joid: params.joid,
          last_acceptable_ts: params.lastAcceptableTs,
          prids: params.prids,
        },
      }),
      transformResponse: getShortlistInfoByPridsResponse,
    }),
    importPeopleListProfile: build.mutation<
      IPeopleListImportProfileResponse,
      IImportProfileParams
    >({
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joId,
          url: `${sourcingUrl}/api/candidates`,
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: {
          ...(params.child_icp_id && { child_icp_id: params.child_icp_id }),
          icpid: params.icpId,
          ids: params.prIds,
          joid: params.joId,
          public_profile: params.publicProfile,
          sandbox: params.sandbox,
          type: "Import",
          uid: params.uid,
        },
      }),
    }),
    shortlistPeopleListProfile: build.mutation<
      unknown,
      IPeopleListShortlistProfileParams
    >({
      invalidatesTags: (_result, _error, params) => [
        {
          id: params.icpid,
          type: "PeopleListIcps",
        },
        {
          id: params.icpid,
          type: "PeopleListShortlistInfo",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `${matchesUrl}/hm/api/icp`,
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: {
          ...(params.child_icp_id && { child_icp_id: params.child_icp_id }),
          icpid: params.icpid,
          ignore_ats_check: "true",
          is_copilot_tab: false,
          joid: params.joid,
          prid: params.prid,
          shortlisted: params.shortlisted,
          sl_from_sandbox: params.sl_from_sandbox,
          source: params.source || "MatchesPortal",
          sub_type: "Shortlist",
          type: "SaveMatchFeedback",
        },
      }),
    }),
    saveAttrCandidateTags: build.mutation<unknown, IAttrCandidateTagsParams>({
      invalidatesTags: (_result, _error, { sourceId }) => [
        {
          id: sourceId,
          type: "getAttrCandidateTags",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `${matchesUrl}/hm/api/attribution_event/save_candidate_tags`,
        }),
        credentials: "include",
        method: "POST",
        body: {
          account_id: params.accountId,
          prid: params.prid,
          source_id: params.sourceId,
          tags: params.tags,
        },
      }),
    }),
    saveAttrCandidateTagsBulk: build.mutation<
      unknown,
      IAttrCandidateTagsBulkUpdateParams
    >({
      invalidatesTags: (_result, _error, { sourceId }) => [
        {
          id: sourceId,
          type: "getAttrCandidateTags",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `${matchesUrl}/hm/api/attribution_event/save_candidate_tags_bulk`,
        }),
        credentials: "include",
        method: "POST",
        body: {
          account_id: params.accountId,
          prids: params.prids,
          source_id: params.sourceId,
          tags: params.tags,
        },
      }),
    }),
    getAttrCandidateTags: build.query<
      CandidateTagsByPrid,
      IGetAttrCandidateTagsParams
    >({
      providesTags: (_result, _error, { sourceId }) => [
        {
          id: sourceId,
          type: "getAttrCandidateTags",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `${matchesUrl}/hm/api/attribution_event/get_candidate_tags`,
        }),
        credentials: "include",
        method: "POST",
        body: {
          account_id: params.accountId,
          prids: params.prids,
          source_id: params.sourceId,
        },
      }),
    }),
    getAttributeEventsViews: build.query<
      Record<string, number>,
      {
        eventTab?: string;
        ignoreUser?: boolean;
        includeCandidateTags?: boolean;
        joid?: string;
        sourceId: string;
      }
    >({
      query: (params) => ({
        url: getProfileEventsUrl(params),
        credentials: "include",
        method: "GET",
      }),
      transformResponse: (response: Record<string, number>) =>
        Object.fromEntries(
          Object.entries(response || {}).sort(
            ([, firstCount], [, secondCount]) => secondCount - firstCount
          )
        ),
    }),
    saveSearchSetting: build.mutation<IIcpSettings, IIcpSettingsPayload>({
      invalidatesTags: (_result, _error, { icpId }) => [
        {
          id: icpId,
          type: "searchSettings",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `${matchesUrl}/hm/api/search_settings`,
        }),
        credentials: "include",
        method: "POST",
        body: {
          icp_id: params.icpId,
          ...(params.candidate_tags && {
            candidate_tags: params.candidate_tags,
          }),
          ...(params.company_coverage && {
            company_coverage: params.company_coverage,
          }),
          ...(params.company_coverage_reporting && {
            company_coverage_reporting: params.company_coverage_reporting,
          }),
        },
      }),
    }),
    getSearchSettings: build.query<
      IIcpSettings | null,
      { icpId: string; joid?: string }
    >({
      providesTags: (_result, _error, { icpId }) => [
        {
          id: icpId,
          type: "searchSettings",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `${matchesUrl}/hm/api/search_settings?icp_id=${params.icpId}`,
        }),
        credentials: "include",
        method: "GET",
      }),
    }),
    getPeopleListScorecard: build.query<
      IPeopleListScorecardDocument,
      { ref: string; joid?: string }
    >({
      providesTags: (_result, _error, params) => [
        {
          id: params.ref,
          type: "PeopleListScorecard",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: "pub/api/scorecards",
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
        params: {
          ref: params.ref,
        },
      }),
    }),
    generatePeopleListScorecard: build.mutation<
      IPeopleListScorecardDocument[],
      IPeopleListScorecardGenerateParams
    >({
      query: (params) => ({
        url: "pub/api/generate-scorecards",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        params: {
          joid: params.joid,
        },
        body: {
          icp_requirements: params.icp_requirements,
          no_persist: params.no_persist,
          text_prompt: params.text_prompt,
        },
      }),
    }),
    createPeopleListScorecard: build.mutation<
      IPeopleListScorecardDocument,
      IPeopleListScorecardSaveParams
    >({
      invalidatesTags: ["PeopleListScorecard"],
      query: (params) => ({
        url: "pub/api/scorecards",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        params: {
          joid: params.joid,
        },
        body: {
          scorecards: params.scorecards,
          text_prompt: params.text_prompt,
        },
      }),
    }),
    updatePeopleListScorecard: build.mutation<
      IPeopleListScorecardDocument,
      IPeopleListScorecardSaveParams & { ref: string }
    >({
      invalidatesTags: (_result, _error, params) => [
        {
          id: params.ref,
          type: "PeopleListScorecard",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `pub/api/scorecards/${params.ref}`,
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: {
          auto_generate_shortforms: params.auto_generate_shortforms,
          scorecards: params.scorecards,
          text_prompt: params.text_prompt,
        },
      }),
    }),
    getPeopleListScorecardProjectDetails: build.query<
      IPeopleListScorecardProjectDetails,
      { icpId: string; joid?: string }
    >({
      providesTags: (_result, _error, params) => [
        {
          id: params.icpId,
          type: "PeopleListScorecardProjectDetails",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: "pub/api/project-details",
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
        params: {
          icp_id: params.icpId,
        },
      }),
    }),
    createPeopleListScorecardProjectDetails: build.mutation<
      IPeopleListScorecardProjectDetails,
      IPeopleListScorecardProjectDetailsSaveParams
    >({
      invalidatesTags: (_result, _error, params) => [
        {
          id: params.icp_id,
          type: "PeopleListScorecardProjectDetails",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: "pub/api/project-details",
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: {
          enable_ats_notes_for_scorecarding:
            params.enable_ats_notes_for_scorecarding,
          enable_scorecards: params.enable_scorecards,
          icp_id: params.icp_id,
          mode: params.mode,
          scorecard_ref: params.scorecard_ref,
        },
      }),
    }),
    updatePeopleListScorecardProjectDetails: build.mutation<
      IPeopleListScorecardProjectDetails,
      IPeopleListScorecardProjectDetailsSaveParams
    >({
      invalidatesTags: (_result, _error, params) => [
        {
          id: params.icp_id,
          type: "PeopleListScorecardProjectDetails",
        },
      ],
      query: (params) => ({
        url: appendJoidToUrl({
          joid: params.joid,
          url: `pub/api/project-details/${params.icp_id}`,
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: {
          enable_ats_notes_for_scorecarding:
            params.enable_ats_notes_for_scorecarding,
          enable_scorecards: params.enable_scorecards,
          mode: params.mode,
          scorecard_ref: params.scorecard_ref,
        },
      }),
    }),
    evaluatePeopleListScorecard: build.mutation<
      IPeopleListScorecardEvaluationResult,
      { joid: string; prids: string[]; ref: string }
    >({
      query: (params) => ({
        url: "pub/api/evaluate-scorecards",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        params: {
          joid: params.joid,
        },
        body: {
          prids: params.prids,
          ref: params.ref,
        },
      }),
    }),
  }),
});

export const {
  endpoints,
  // Company Builder
  useCreateCompanyCompMacrosMutation,
  useCreatePeopleListScorecardMutation,
  useCreatePeopleListScorecardProjectDetailsMutation,
  // RRA
  useGetAssignmentRoleDetailsQuery,
  useEvaluatePeopleListScorecardMutation,
  useGeneratePeopleListScorecardMutation,
  useGetAttrCandidateTagsQuery,
  useGetAttributeEventsViewsQuery,
  useGetIcpInsightsQuery,
  useGetMatchesAwaitMutation,
  useGetMultiMatchesQuery,
  useGetPeopleListScorecardProjectDetailsQuery,
  useGetPeopleListScorecardQuery,
  useGetPeopleListIcpsQuery,
  useGetSearchByIdQuery,
  useGetSearchSettingsQuery,
  useGetShortlistInfoByPridsQuery,
  useImportPeopleListProfileMutation,
  useLazyGetAttrCandidateTagsQuery,
  useLazyGetAttributeEventsViewsQuery,
  useLazyGetIcpInsightsQuery,
  useLazyGetPeopleListIcpsQuery,
  useLazyGetProfileIdByCandidateRefQuery,
  useLazyGetRRAProfilesQuery,
  useLazyGetSearchSettingsQuery,
  useSaveAttrCandidateTagsBulkMutation,
  useSaveAttrCandidateTagsMutation,
  useSaveSearchSettingMutation,
  useShortlistPeopleListProfileMutation,
  useUpdateIcpInsightsMutation,
  useUpdatePeopleListScorecardMutation,
  useUpdatePeopleListScorecardProjectDetailsMutation,
} = searchesApi;
